import { ExternalQuestion, RespondentAnswerTypes } from '@sm/question-widgets/respondent-survey';
import {
  CheckBoxAnswerType,
  DropdownAnswerType,
  MatrixAnswerType,
  OpenEndedAnswerType,
  MultipleChoiceAnswerType,
  ImageChoiceAnswerType,
  ExternalMultipleChoiceRespondentSurvey,
  NpsAnswerType,
  StarRatingAnswerType,
  ResponseValue,
} from '@sm/question-definitions';
import { QuestionAnswers } from '~app/pages/SurveyTaking/v2/types';
import { ExistingAnswers, SurveyQuestionFamily } from '~lib/generatedGqlTypes';

const mapOpenEndedAnswerValues = (responses: ExistingAnswers[]): OpenEndedAnswerType['values'] => {
  const firstResponse = responses && responses.length > 0 ? responses[0] : null;

  if (!firstResponse?.answerText) {
    return [];
  }

  return [{ id: firstResponse.questionId, value: firstResponse.answerText }];
};

const mapMatrixRadioAnswerValues = (responses: ExistingAnswers[]): MatrixAnswerType['values'] => {
  return responses
    .filter(response => !!response.answerId && !!response.answerNum)
    .map(response => ({
      id: response.answerId as string,
      value: [{ id: response.answerNum as string, value: response.answerNum as string }],
    }));
};

const mapManyMatrixCheckboxAnswerValues = (responses: ExistingAnswers[]): MatrixAnswerType['values'] => {
  const responseMap = new Map<string, ResponseValue<string>[]>();

  responses.forEach(({ answerId, answerNum }) => {
    if (answerId && answerNum) {
      if (!responseMap.has(answerId)) {
        responseMap.set(answerId, []);
      }

      const answerArray = responseMap.get(answerId);
      answerArray?.push({ id: answerNum, value: answerNum });
    }
  });

  return Array.from(responseMap.entries()).map(([id, value]) => ({ id, value }));
};

const mapMatrixSingleRowMenuAnswerValues = (responses: ExistingAnswers[]): MatrixAnswerType['values'] => {
  return responses
    .filter(response => !!response.answerId && !!response.answerNum && !!response.answerType)
    .map(response => ({
      id: response.answerId as string,
      value: [{ id: response.answerNum as string, value: response.answerType as string }],
    }));
};

const findMappedResponse = (responses: ExistingAnswers[]): ResponseValue | null => {
  const response = responses.find(r => r.answerId);
  if (response) {
    return {
      id: response.answerId ?? '',
      value: response.answerId ?? '',
    } as ResponseValue;
  }

  return null;
};

const findMappedOtherResponse = (
  responses: ExistingAnswers[],
  question: Pick<ExternalMultipleChoiceRespondentSurvey, 'id' | 'answerOther'>
): ResponseValue | null => {
  if (question.answerOther) {
    const otherResponse = responses.find(r => r.answerText);
    if (otherResponse) {
      return {
        id: question.answerOther.id,
        value: otherResponse.answerText ?? '',
        type: question.answerOther.type,
      } as ResponseValue;
    }
  }

  return null;
};

const mapMultipleChoiceAnswerValues = (
  responses: ExistingAnswers[],
  question: Pick<ExternalMultipleChoiceRespondentSurvey, 'id' | 'answerOther'>
): MultipleChoiceAnswerType['values'] => {
  const mappedResponse = findMappedResponse(responses);
  const mappedOtherResponse = findMappedOtherResponse(responses, question);
  if (mappedOtherResponse) {
    if (question.answerOther?.type === 'ANSWER') {
      return [mappedOtherResponse];
    }

    if (question.answerOther?.type === 'COMMENT') {
      return mappedResponse ? [mappedResponse, mappedOtherResponse] : [mappedOtherResponse];
    }
  }

  return mappedResponse ? [mappedResponse] : [];
};

function mapSingleAnswerDropdown(responses: ExistingAnswers[]): DropdownAnswerType['values'] {
  const firstResponse = responses && responses.length > 0 ? responses[0] : null;

  if (!firstResponse?.answerId) {
    return [];
  }

  return [{ id: firstResponse.answerId, value: firstResponse.answerId }];
}

const mapCheckBoxAnswerValues = (responses: ExistingAnswers[]): CheckBoxAnswerType['values'] => {
  return responses
    .filter(response => !!response.answerId)
    .map(response => ({
      id: response.answerId as string,
      value: response.answerId as string,
    }));
};

const mapSingleAnswerImageValue = (responses: ExistingAnswers[]): ImageChoiceAnswerType['values'] => {
  const [response] = responses;
  if (response) {
    return [
      {
        id: response.answerId ?? '',
        value: response.answerId ?? '',
      },
    ];
  }

  return [];
};

const mapMultipleAnswerImageValue = (responses: ExistingAnswers[]): ImageChoiceAnswerType['values'] => {
  return responses
    .filter(response => !!response.answerId)
    .map(imgCBResponse => ({
      id: imgCBResponse.answerId as string,
      value: imgCBResponse.answerId as string,
    }));
};

const mapNPSAnswerValues = (responses: ExistingAnswers[]): NpsAnswerType['values'] => {
  return responses
    .filter(r => !!r.answerNum && !!r.answerId)
    .map(r => ({
      id: r.answerId as string,
      value: r.answerNum as string,
    })) as NpsAnswerType['values'];
};

const mapStarRatingAnswerValues = (responses: ExistingAnswers[]): StarRatingAnswerType['values'] => {
  return responses
    .filter(r => !!r.answerNum && !!r.answerId)
    .map(r => ({
      id: r.answerId as string,
      value: r.answerNum as string,
    })) as StarRatingAnswerType['values'];
};

const mapAnswerValues = (responses: ExistingAnswers[], question: ExternalQuestion): RespondentAnswerTypes['values'] => {
  const { family, variant } = question;

  switch (family as SurveyQuestionFamily) {
    case 'OPEN_ENDED': {
      return mapOpenEndedAnswerValues(responses);
    }
    case 'MULTIPLE_CHOICE': {
      // variant SINGLE_ANSWER_MENU (dropdown) | SINGLE_ANSWER_RADIO
      if (variant === 'SINGLE_ANSWER_RADIO') {
        return mapMultipleChoiceAnswerValues(responses, question);
      }

      if (variant === 'SINGLE_ANSWER_IMAGE') {
        return mapSingleAnswerImageValue(responses);
      }

      if (variant === 'MANY_ANSWERS_CHECKBOX') {
        return mapCheckBoxAnswerValues(responses);
      }

      if (variant === 'MANY_ANSWERS_IMAGE') {
        return mapMultipleAnswerImageValue(responses);
      }

      if (variant === 'SINGLE_ANSWER_MENU') {
        return mapSingleAnswerDropdown(responses);
      }

      return [];
    }
    case 'RATING_SCALE': {
      // NPS QuestionType
      if (variant === 'NPS') {
        return mapNPSAnswerValues(responses);
      }
      // StarRating QuestionType
      if (variant === 'SINGLE_ANSWER_SYMBOL') {
        return mapStarRatingAnswerValues(responses);
      }
      return [];
    }
    case 'MATRIX':
      if (variant === 'SINGLE_ANSWER_RADIO') {
        return mapMatrixRadioAnswerValues(responses);
      }

      if (variant === 'MANY_ANSWERS_CHECKBOX') {
        return mapManyMatrixCheckboxAnswerValues(responses);
      }

      if (variant === 'MENU') {
        return mapMatrixSingleRowMenuAnswerValues(responses);
      }

      return [];
    default: {
      if (process.env.NODE_ENV === 'development') {
        /**
         * This is a logging that would only happen during development. It is not expected to happen in production as it will be removed by the compiler.
         */
        console.debug(`Question type not mapped: ${family}, ${variant}`);
      }
      return [];
    }
  }
};

/**
 * Converts server responses to application state answers.
 *
 * @param {Partial<RespApiAnswerInput>[]} responses - The list of responses from the server.
 * @param {ExternalQuestion[]} questions - The list of questions on the current page.
 * @returns {QuestionAnswers} - The mapped answers in the application state format.
 */
export const mapResponseToSurveyAnswers = (
  responses: ExistingAnswers[],
  questions: ExternalQuestion[]
): QuestionAnswers => {
  return questions.reduce<QuestionAnswers>((acc, question) => {
    const backendResponses = responses.filter(response => response.questionId === question.id);

    // no need to map if there are no responses
    if (backendResponses.length === 0) {
      return acc;
    }
    const values = mapAnswerValues(backendResponses, question);

    acc[question.id] = {
      questionId: question.id,
      values,
      isDirty: false,
      touched: false,
      family: question.family,
      variant: question.variant,
    };
    return acc;
  }, {});
};

export default mapResponseToSurveyAnswers;
